import * as Yup from 'yup';
import React from 'react';
import { Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { useInstitutionSetting } from '../../../components/use-institution-settings';
import { Institution } from '../../../../../global/models/institution-model';
import { InstitutionSurveyFormModel } from './institution-survey-model';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { ApiStatuses } from '../../../../../global/models/api-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { parseLov } from '../../../../../global/helpers/form-data-helper';
import { LayPensionPlanOptions, YesNoOptions, YesNoOptionsFull } from '../../../../../global/constants/lists';
import { getRequiredText } from '../../../../../global/helpers/transformer';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { FormBaseModel } from '../../../../../global/models/page-model';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';


interface InstitutionSurveyFormProps extends FormBaseModel {
	onSubmitHandler: (savedValues: any, values: any) => any;
	institutionDetail: Institution,
}
export function InstitutionSurveyForm(props: InstitutionSurveyFormProps) {

	const {
		onSubmitHandler,
		institutionDetail,
	} = props;

	const initialValues: InstitutionSurveyFormModel = {
		school_indicator: institutionDetail.school_indicator || 'N',
		parish_description: institutionDetail.parish_description,
		org_type: institutionDetail.org_type,
		seating_capacity: institutionDetail.seating_capacity,
		service_times: institutionDetail.service_times,
		special_programs: institutionDetail.special_programs,
		welcome_mission_statement: institutionDetail.welcome_mission_statement,
		provides_lay_pension_plan: institutionDetail.provides_lay_pension_plan || 'No',
		lay_pension_plan_type: institutionDetail.lay_pension_plan_type,
	};


	const success = useSelector<TcsmStates, ApiStatuses>(state => state.institutionState.institutionSurveyFormStatus);
	const institutionTypes = useSelector<TcsmStates, any>(state => state.lovProcState.orgLov.org_type);
	const institutionTypesOptions = institutionTypes ? parseLov(institutionTypes) : [];

	const institutionSettings = useInstitutionSetting();
	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
	const apiErrors = useApiResponseError(FormTypes.INSTITUTION_SURVEY);
	
	const validationSchema = Yup.object().shape({
		org_type: Yup.string().required(getRequiredText(labelSetting.requiredFormat, institutionSettings.institutionTypeLabel)),
		seating_capacity: Yup.number().typeError(institutionSettings.seatingCapacityTypeErrorMessage || 'Seating capacity must be a number').integer().min(1, institutionSettings.seatingCapacityErrorMessage),
		lay_pension_plan_type: Yup.string().ensure().when('provides_lay_pension_plan', {
			is: 'Yes',
			then: Yup.string().required(institutionSettings.layPensionPlanTypeErrorMessage),
		}),
	});

	const gpsItem = getGpsType(GpsType.INSTITUTION_SURVEY, null);


	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values) => onSubmitHandler(initialValues, values)}
			validationSchema={validationSchema}
			enableReinitialize
			initialErrors={apiErrors}
		>
			{(formProps: FormikProps<InstitutionSurveyFormModel>) => (
				<Form>
					<FormWrapper
						formikProps={formProps}
						submitText={institutionSettings.submitText}
						cancelText={institutionSettings.cancelText}
						onCancel={() => {
							formProps.resetForm();
							window.scrollTo(0, 0);
						}}
						successfulForms={success === ApiStatuses.Success ? [FormTypes.INSTITUTION_SURVEY] : []}
					>
						<PageHeader
							headerText={props.formHeader}
							description={props.formDescription}
						/>
						<Hidden mdUp>
							<SideBarContent>
								<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
							</SideBarContent>
						</Hidden>
						<DividerSection removeDivider>
							<FormGroup>
								<FormField
									name='org_type'
									label={institutionSettings.institutionTypeLabel}
									component='input'
									options={institutionTypesOptions}
									errors={formProps.errors}
									touched={formProps.touched}
									select
									required
								/>
								<div className='form-item'>
									<p className='input-label'>{institutionSettings.yearEstablishedLabel} </p>
									<p>{institutionDetail?.founded_year}</p>
								</div>
								<div className='form-item'>
									<p className='input-label'>{institutionSettings.memberShipSizeLabel}</p>
									<p>{institutionDetail?.congregation_size?.toString()}</p>
								</div>

								<FormField
									name='seating_capacity'
									label={institutionSettings.seatingCapacityLabel}
									component='input'
									autoWidth
									errors={formProps.errors}
									touched={formProps.touched}

								/>
								<FormField
									name='service_times'
									label={institutionSettings.houseOfServiceLabel}
									component='input'
									multiline
									rows={4}
									errors={formProps.errors}
									touched={formProps.touched}
								/>
								<FormField
									name='school_indicator'
									label={institutionSettings.daySchoolQuestion}
									component="radioGroup"
									options={YesNoOptions}
									touched={formProps.touched}
									errors={formProps.errors}
									required
								/>

								<FormField
									name='provides_lay_pension_plan'
									label={institutionSettings.layPensionPlanQuestion}
									component="radioGroup"
									options={YesNoOptionsFull}
									touched={formProps.touched}
									errors={formProps.errors}
									required
								/>
								{
									formProps.values.provides_lay_pension_plan === 'Yes' &&
									<FormField
										name='lay_pension_plan_type'
										label={institutionSettings.typeOfLayPensionPlanQuestion}
										component='input'
										options={LayPensionPlanOptions}
										errors={formProps.errors}
										touched={formProps.touched}
										select
										required
									/>
								}
								<FormField
									name='welcome_mission_statement'
									label={institutionSettings.welcomeMessageLabel}
									component='input'
									multiline
									rows={4}
									errors={formProps.errors}
									touched={formProps.touched}
								/>
								<FormField
									name='parish_description'
									label={institutionSettings.briefDescriptionLabel}
									component='input'
									multiline
									rows={4}
									errors={formProps.errors}
									touched={formProps.touched}
								/>
								<FormField
									name='special_programs'
									label={institutionSettings.specialProgramLabel}
									component='input'
									multiline
									rows={4}
									errors={formProps.errors}
									touched={formProps.touched}
								/>
							</FormGroup>
						</DividerSection>
					</FormWrapper>
				</Form>)}
		</Formik >
	);
}